import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    // Unreadable or malformed entry — fall back rather than break the view.
    return fallback;
  }
}

/**
 * useState that survives unmounting for the rest of the browser tab's session.
 *
 * For view state that is expensive to rebuild but not worth putting in the URL — a
 * prediction run the user waited on, say — so opening a profile and pressing Back
 * doesn't throw it away. Values must be JSON-serializable.
 */
export function useSessionState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readStored(key, initial));

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Over quota or storage blocked — persistence is a convenience, not a requirement.
    }
  }, [key, value]);

  return [value, setValue];
}
